// src/pages/reports/ReportsPM.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Spinner,
  IconButton,
  Tooltip,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Badge,
  Spacer,
  ButtonGroup,
} from '@chakra-ui/react';
import {
  EmailIcon,
  CopyIcon,
  InfoOutlineIcon,
  ExternalLinkIcon,
  CloseIcon,
  ArrowUpDownIcon,
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import useOrgClaims from '../../hooks/useOrgClaims';
import { db } from '../../firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';

import {
  DEFAULT_TEMPLATES,
  fill,
  buildVars,
  normalizeRecipients,
  makeMailtoUrl,
} from '../../lib/templates';

import PMDrawer from './components/PMDrawer';
import { saveCallAttempt, markDnc } from './services/callbacks.service';

// ---------- Small helpers ----------
const fmtDateTimeShort = (ts) => {
  if (!ts) return 'No due';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return 'No due';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
};

const fmtDateShort = (ts) => {
  if (!ts) return '—';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};
const fmtTime24 = (ts) => {
  if (!ts) return '—';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mi}`;
};

const displayNameFrom = (lead) => {
  const titled = lead.customerTitle ? `${lead.customerTitle} ` : '';
  const whole =
    lead.customerFullName ||
    `${lead.customerFirstName || ''} ${lead.customerLastName || ''}`.trim();
  return (titled + (whole || '—')).trim();
};

const dueFromLead = (lead) =>
  lead?.nextAction?.dueAt || lead?.lastCallbackNextCallDate || null;

const attemptCountFromLead = (lead) =>
  typeof lead?.attemptCount === 'number' ? lead.attemptCount : 0;

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function classifyDue(due) {
  if (!due) return 'none';
  const d = typeof due?.toDate === 'function' ? due.toDate() : new Date(due);
  if (Number.isNaN(d.getTime())) return 'none';
  const now = new Date();
  if (d < now && !isSameDay(d, now)) return 'overdue';
  if (isSameDay(d, now)) return 'today';
  return 'future';
}

function colourForDue(due) {
  const k = classifyDue(due);
  if (k === 'overdue') return 'red.500';
  if (k === 'today') return 'orange.500';
  return 'gray.700';
}

// +2 business days, 10:00 (ISO "YYYY-MM-DDTHH:mm")
function defaultNextCallLocal() {
  const d = new Date();
  let add = 2;
  while (add > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) add -= 1;
  }
  d.setHours(10, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

// Reason codes → labels (kept in sync with the mobile modal)
const REASON_LABELS_BY_CODE = {
  think_about_it: 'Think about it',
  cant_afford: "Can’t afford it",
  competitor_better: 'Better offer from competitors',
  want_more_quotes: 'Want more quotes',
  single_head: 'Single Head',
};

// ---------- Component ----------
export default function ReportsPM() {
  const { orgId, user } = useOrgClaims();
  const toast = useToast();
  const navigate = useNavigate();

  const [orgProfile, setOrgProfile] = useState({});
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [search, setSearch] = useState('');

  // Filters & sort
  const [dueFilter, setDueFilter] = useState('all'); // all | overdue | today | next7
  const [fromDate, setFromDate] = useState(''); // datetime-local
  const [toDate, setToDate] = useState('');
  const [sortAsc, setSortAsc] = useState(true); // sort by Due asc/desc

  // PM drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('details');
  const [activeLead, setActiveLead] = useState(null);
  const [pmDoc, setPmDoc] = useState(null);
  const [pmNotes, setPmNotes] = useState([]); // conversation history
  // const [quote, setQuote] = useState(null); // REMOVED
  // const [quoteLoading, setQuoteLoading] = useState(false); // REMOVED

  // DNC modal
  const [dncOpen, setDncOpen] = useState(false);
  const [dncLead, setDncLead] = useState(null);
  const [dncReason, setDncReason] = useState('Do Not Contact');
  const [dncNote, setDncNote] = useState('');

  // Log form
  const [called, setCalled] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [priceOffered, setPriceOffered] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [nextCallDate, setNextCallDate] = useState('');

  // Cache PM snapshots for rows we render (keyed by leadId)
  const [pmCache, setPmCache] = useState({});

  // org + templates
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        setOrgProfile(orgSnap.exists() ? orgSnap.data() || {} : {});
        const tSnap = await getDoc(doc(db, 'organizations', orgId, 'settings', 'templates'));
        setTemplates(
          tSnap.exists() ? { ...DEFAULT_TEMPLATES, ...tSnap.data() } : DEFAULT_TEMPLATES
        );
      } catch {}
    })();
  }, [orgId]);

  // Fetch batch
  const fetchBatch = useCallback(
    async (afterDocRef = null) => {
      if (!orgId) return { items: [], lastDoc: null };

      const baseFilters = [where('status', 'in', ['PM', 'PMDNH'])];
      const baseQ = query(
        collection(db, 'organizations', orgId, 'leads'),
        ...baseFilters,
        orderBy('createdAt', 'desc'),
        limit(25)
      );
      const qPaged = afterDocRef ? query(baseQ, startAfter(afterDocRef)) : baseQ;

      const snap = await getDocs(qPaged);
      if (snap.empty) return { items: [], lastDoc: null };

      const items = [];
      for (const d of snap.docs) {
        const x = { id: d.id, ...(d.data() || {}) };
        const isFinished = Boolean(x.apptEndAt);
        if (!isFinished) continue;

        items.push({
          id: d.id,
          leadId: d.id,
          status: (x.status || '').toUpperCase(),
          apptDate: x.apptStartAt || null,
          salespersonId: x.salespersonId || '',
          salespersonName: x.salespersonName || x.salespersonId || 'Sales',
          customer: displayNameFrom(x),
          postcode: x.postcode || x.addressPostcode || '—',
          attemptCount: attemptCountFromLead(x),
          dueAt: dueFromLead(x),
        });
      }
      return { items, lastDoc: snap.docs[snap.docs.length - 1] || null };
    },
    [orgId]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { items, lastDoc } = await fetchBatch();
        if (alive) {
          setRows(items);
          setCursor(lastDoc);
        }
      } catch (e) {
        toast({ title: 'Failed to load', description: e?.message || String(e), status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchBatch, orgId, toast]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const { items, lastDoc } = await fetchBatch(cursor);
      setRows((prev) => prev.concat(items));
      setCursor(lastDoc);
    } finally {
      setLoadingMore(false);
    }
  }

  // Open PM drawer
  async function openDrawer(row) {
    setActiveLead(row);
    setInitialTab('details');

    setCalled(false);
    setAnswered(false);
    setPriceOffered(false);
    setNewPrice('');
    setNotes('');
    setNextCallDate(defaultNextCallLocal());
    setPmNotes([]); // reset each open

    try {
      // 1) pm current doc
      const pmRef = doc(db, 'organizations', orgId, 'leads', row.leadId, 'pmReport', 'current');
      const pmSnap = await getDoc(pmRef);
      setPmDoc(pmSnap.exists() ? pmSnap.data() || {} : {});
    } catch {
      setPmDoc({});
    }

    try {
      // 2) conversation notes (latest first)
      const notesQ = query(
        collection(db, 'organizations', orgId, 'leads', row.leadId, 'pmNotes'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const ns = await getDocs(notesQ);
      setPmNotes(ns.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    } catch {
      setPmNotes([]);
    }

    try {
      // 3) load lead phone (click-to-call in drawer)
      const leadRef = doc(db, 'organizations', orgId, 'leads', row.leadId);
      const leadSnap = await getDoc(leadRef);
      if (leadSnap.exists()) {
        const L = leadSnap.data() || {};
        const phone =
          L.customerPhone ||
          L.phone ||
          L.mobile ||
          L.tel ||
          (L.contact && L.contact.phone) ||
          (L.phones && L.phones.primary) ||
          '';
        setActiveLead((prev) => ({ ...prev, phone }));
      }
    } catch {
      // ignore
    }

    // optional quote fetch (kept; may remove later)
    // REMOVED: setQuote(null);
    // REMOVED: setQuoteLoading(false);
    // The PMDrawerAccordion component now handles its own quote fetching.

    setDrawerOpen(true);
  }

  // Email / SMS (unchanged)
  function handleEmail(row) {
    const vars = buildVars({
      lead: {
        id: row.leadId,
        customerFullName: row.customer,
        customerFirstName: row.customer?.split(' ')?.[0] || '',
        customerEmail: '',
        customerPhone: '',
        postcode: row.postcode,
        salespersonName: row.salespersonName,
        apptStartAt: row.apptDate,
      },
      org: orgProfile,
    });
    const t = templates?.pm || DEFAULT_TEMPLATES.pm;
    const subject = fill(t.emailSubjectTemplate, vars);
    const body = fill(t.emailBodyTemplate, vars);
    const recipients = normalizeRecipients(t.defaultRecipients, '');
    const href = makeMailtoUrl(recipients, subject, body);
    if (href.length > 1800) {
      navigator.clipboard.writeText(body).catch(() => {});
      window.location.href = makeMailtoUrl(recipients, subject, '');
    } else {
      window.location.href = href;
    }
  }

  async function handleText(row) {
    try {
      const vars = buildVars({
        lead: {
          id: row.leadId,
          customerFullName: row.customer,
          customerFirstName: row.customer?.split(' ')?.[0] || '',
          customerPhone: '', // phone is shown in drawer, not list
          postcode: row.postcode,
          salespersonName: row.salespersonName,
          apptStartAt: row.apptDate,
        },
        org: orgProfile,
      });
      const t = templates?.pm || DEFAULT_TEMPLATES.pm;
      const text = fill(t.smsTemplate || '', vars);
      await navigator.clipboard.writeText(text);
      toast({ title: 'SMS copied', description: 'Text ready to paste.', status: 'success' });
    } catch (e) {
      toast({ title: 'Copy failed', description: e?.message || 'Could not copy.', status: 'error' });
    }
  }

  // DNC (unchanged)
  function openDnc(row) {
    setDncLead(row);
    setDncReason('Do Not Contact');
    setDncNote('');
    setDncOpen(true);
  }
  function closeDnc() {
    setDncOpen(false);
    setDncLead(null);
  }
  async function handleConfirmDnc() {
    try {
      await markDnc({ orgId, leadId: dncLead.leadId, reason: dncReason, note: dncNote, user });
      setRows((prev) => prev.filter((r) => r.leadId !== dncLead.leadId));
      closeDnc();
      toast({ title: 'Marked as Do Not Contact', status: 'success' });
    } catch (e) {
      toast({ title: 'Failed', description: e?.message || String(e), status: 'error' });
    }
  }

  // Save call attempt — also append a PM note (user + time + optional newPrice)
  const handleSaveAttempt = useCallback(async () => {
    if (!activeLead) return;
    try {
      await saveCallAttempt({
        orgId,
        leadId: activeLead.leadId,
        user,
        payload: { called, answered, priceOffered, newPrice, notes, nextCallDate },
      });

      // Persist notes in a pmNotes timeline (only if provided)
      const trimmed = (notes || '').trim();
      if (trimmed) {
        const priceNum = Number(newPrice);
        const hasPrice = priceOffered && Number.isFinite(priceNum) && priceNum >= 0;

        const payload = {
          text: trimmed,
          createdAt: serverTimestamp(),
          createdByUid: user?.uid || null,
          createdByName: user?.displayName || user?.email || 'User',
        };
        if (hasPrice) payload.newPrice = priceNum;

        const ref = await addDoc(
          collection(db, 'organizations', orgId, 'leads', activeLead.leadId, 'pmNotes'),
          payload
        );

        // Optimistic UI: prepend newest note
        setPmNotes((prev) => [
          {
            id: ref.id,
            text: trimmed,
            createdAt: new Date(),
            createdByUid: user?.uid || null,
            createdByName: user?.displayName || user?.email || 'User',
            ...(hasPrice ? { newPrice: priceNum } : {}),
          },
          ...prev,
        ]);
      }

      // UI-only: attempts +1, dueAt set from nextCallDate (if provided)
      setRows((prev) =>
        prev.map((r) =>
          r.leadId === activeLead.leadId
            ? {
                ...r,
                attemptCount: (r.attemptCount || 0) + 1,
                dueAt: nextCallDate ? new Date(nextCallDate).toISOString() : r.dueAt || null,
              }
            : r
        )
      );
      toast({ title: 'Call logged', status: 'success' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.message || String(e), status: 'error' });
    }
  }, [
    activeLead,
    orgId,
    user,
    called,
    answered,
    priceOffered,
    newPrice,
    notes,
    nextCallDate,
    toast,
  ]);

  // Open Sales Report page for the active lead
  function openSalesReportForActive() {
    if (!activeLead) return;
    const back = encodeURIComponent('/reports/opportunities');
    navigate(`/reports/sales-report?leadId=${encodeURIComponent(activeLead.leadId)}&back=${back}`);
  }

  // ---------- Client-side search, filter, sort ----------
  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const now = new Date();

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const inQuick = (due) => {
      if (dueFilter === 'all') return true;
      const k = classifyDue(due);
      if (dueFilter === 'overdue') return k === 'overdue';
      if (dueFilter === 'today') return k === 'today';
      if (dueFilter === 'next7') {
        if (!due) return false;
        const d = typeof due?.toDate === 'function' ? due.toDate() : new Date(due);
        const inNext7 = d >= now && d <= new Date(now.getTime() + 7 * 24 * 3600 * 1000);
        return inNext7;
      }
      return true;
    };

    const inRange = (due) => {
      if (!from && !to) return true;
      if (!due) return false;
      const d = typeof due?.toDate === 'function' ? due.toDate() : new Date(due);
      if (Number.isNaN(d.getTime())) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };

    const list = rows
      .filter((r) => {
        if (term) {
          const hay = `${r.customer} ${r.postcode}`.toLowerCase();
          if (!hay.includes(term)) return false;
        }
        if (!inQuick(r.dueAt)) return false;
        if (!inRange(r.dueAt)) return false;
        return true;
      })
      .sort((a, b) => {
        const da = a.dueAt ? new Date(a.dueAt) : null;
        const db = b.dueAt ? new Date(b.dueAt) : null;
        if (!da && !db) return 0;
        if (!da) return 1; // No due goes last
        if (!db) return -1;
        return sortAsc ? da - db : db - da;
      });

    return list;
  }, [rows, search, dueFilter, fromDate, toDate, sortAsc]);

  // Load pmReport/current for rows we are about to render (once per leadId)
  useEffect(() => {
    if (!orgId || !filteredSorted?.length) return;
    const idsToLoad = filteredSorted
      .map(r => r.leadId)
      .filter(id => pmCache[id] === undefined);

    if (!idsToLoad.length) return;

    let cancelled = false;
    (async () => {
      for (const id of idsToLoad) {
        try {
          const pmRef = doc(db, 'organizations', orgId, 'leads', id, 'pmReport', 'current');
          const snap = await getDoc(pmRef);
          const data = snap.exists() ? (snap.data() || {}) : {};
          if (!cancelled) setPmCache(prev => ({ ...prev, [id]: data }));
        } catch {
          if (!cancelled) setPmCache(prev => ({ ...prev, [id]: {} }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, filteredSorted, pmCache]);

  // Keybindings: Ctrl/Cmd+S save, Esc close (while drawer open)
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e) {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's';
      if (isSave) {
        e.preventDefault();
        handleSaveAttempt();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, handleSaveAttempt]);

  // ---------- Render ----------
  return (
    <Box>
      <Heading size="lg" color="blue.600" mb={2}>
        Opportunities: PM & PMDNH
      </Heading>
      <Text color="gray.600" mb={4}>
        "Finished" appointments where the customer didn’t buy. Use the Actions to review details, follow up, offer a better deal, etc.
      </Text>

      {/* Controls row */}
      <HStack spacing={3} mb={4} align="center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / postcode"
          maxW="420px"
        />
        <Spacer />
        <ButtonGroup isAttached>
          <Button
            variant={dueFilter === 'overdue' ? 'solid' : 'outline'}
            onClick={() => setDueFilter('overdue')}
          >
            Overdue
          </Button>
          <Button
            variant={dueFilter === 'today' ? 'solid' : 'outline'}
            onClick={() => setDueFilter('today')}
          >
            Today
          </Button>
          <Button
            variant={dueFilter === 'next7' ? 'solid' : 'outline'}
            onClick={() => setDueFilter('next7')}
          >
            Next 7 days
          </Button>
          <Button
            variant={dueFilter === 'all' ? 'solid' : 'outline'}
            onClick={() => setDueFilter('all')}
          >
            All
          </Button>
        </ButtonGroup>
        <HStack>
          <Input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            size="sm"
          />
          <Text fontSize="sm">to</Text>
          <Input
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            size="sm"
          />
        </HStack>
        <Tooltip label={`Sort by Due (${sortAsc ? 'Oldest first' : 'Newest first'})`} openDelay={150}>
          <IconButton
            aria-label="Sort by due"
            icon={<ArrowUpDownIcon />}
            size="sm"
            onClick={() => setSortAsc((v) => !v)}
          />
        </Tooltip>
      </HStack>

      {loading ? (
        <HStack>
          <Spinner />
          <Text>Loading…</Text>
        </HStack>
      ) : (
        <>
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th>Final (£)</Th>
                <Th>Discount (%)</Th>
                <Th>Reasons</Th>
                <Th>Attempts</Th>
                <Th>Due</Th>
                <Th textAlign="right" w="280px">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSorted.map((r) => {
                const dueColour = colourForDue(r.dueAt);
                const pm = pmCache[r.leadId] || {};
                const reasonsFromCodes = Array.isArray(pm.reasonNotBuyingCodes)
                  ? pm.reasonNotBuyingCodes.map((c) => REASON_LABELS_BY_CODE[c]).filter(Boolean)
                  : [];
                const legacyReasons =
                  (!reasonsFromCodes.length && typeof pm.reasonNotBuying === 'string')
                    ? pm.reasonNotBuying.split(',').map((s) => s.trim()).filter(Boolean)
                    : [];
                const labels = reasonsFromCodes.length ? reasonsFromCodes : legacyReasons;
                const show = labels.slice(0, 2);
                const extra = Math.max(0, labels.length - 2);

                return (
                  <Tr key={r.leadId}>
                    {/* CUSTOMER: name + appointment date (dd/mm/yy) + postcode */}
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold">{r.customer}</Text>
                        <Text fontSize="xs" color="gray.700">{fmtDateShort(r.apptDate)}</Text>
                        <Text fontSize="xs" color="gray.600">{r.postcode}</Text>
                      </VStack>
                    </Td>

                    {/* STATUS */}
                    <Td>
                      <Tag colorScheme="orange">{r.status}</Tag>
                    </Td>

                    {/* FINAL (£) */}
                    <Td>
                      <Text>{pm.finalOfferText || pm.priceQuoted || '—'}</Text>
                    </Td>

                    {/* DISCOUNT (%) */}
                    <Td>
                      <Text>{typeof pm.discountPercent === 'number' ? `${pm.discountPercent}%` : '—'}</Text>
                    </Td>

                    {/* REASONS */}
                    <Td>
                      <HStack spacing={1} flexWrap="wrap">
                        {show.map((lbl) => (
                          <Tag key={lbl} size="sm" colorScheme="purple">{lbl}</Tag>
                        ))}
                        {extra > 0 && <Tag size="sm" colorScheme="gray">+{extra}</Tag>}
                        {labels.length === 0 && <Text color="gray.500">—</Text>}
                      </HStack>
                    </Td>

                    {/* ATTEMPTS */}
                    <Td>
                      <Badge variant="subtle" colorScheme="gray">{r.attemptCount ?? 0}</Badge>
                    </Td>

                    {/* DUE */}
                    <Td>
                      <Text fontWeight="medium" color={fmtDateTimeShort(r.dueAt) === 'No due' ? 'gray.500' : dueColour}>
                        {fmtDateTimeShort(r.dueAt)}
                      </Text>
                    </Td>

                    {/* ACTIONS */}
                    <Td textAlign="right" whiteSpace="nowrap">
                      <HStack spacing={2} justify="flex-end" minW="240px">
                        <Tooltip label="DNC" openDelay={150}>
                          <IconButton
                            size="xs"
                            icon={<CloseIcon />}
                            aria-label="DNC"
                            onClick={() => openDnc(r)}
                          />
                        </Tooltip>
                        <Tooltip label="Open" openDelay={150}>
                          <IconButton
                            size="xs"
                            icon={<InfoOutlineIcon />}
                            aria-label="Open"
                            onClick={() => openDrawer(r)}
                          />
                        </Tooltip>
                        <Tooltip label="Email" openDelay={150}>
                          <IconButton
                            size="xs"
                            icon={<EmailIcon />}
                            aria-label="Email"
                            onClick={() => handleEmail(r)}
                          />
                        </Tooltip>
                        <Tooltip label="SMS (copy text)" openDelay={150}>
                          <IconButton
                            size="xs"
                            icon={<CopyIcon />}
                            aria-label="SMS"
                            onClick={() => handleText(r)}
                          />
                        </Tooltip>
                        <Tooltip label="Lead" openDelay={150}>
                          <IconButton
                            size="xs"
                            icon={<ExternalLinkIcon />}
                            aria-label="Open lead"
                            as={RouterLink}
                            to={`/leads/${r.leadId}`}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
              {filteredSorted.length === 0 && (
                <Tr>
                  <Td colSpan={10}>
                    <Text color="gray.600">No PM/PMDNH finished leads match your filters.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          <HStack mt={4}>
            <Button onClick={loadMore} isDisabled={!cursor} isLoading={loadingMore}>
              Load more
            </Button>
            {!cursor && (
              <Text color="gray.500" fontSize="sm">
                End of list
              </Text>
            )}
          </HStack>
        </>
      )}

      {/* PM Drawer */}
      <PMDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initialTab={initialTab}
        lead={activeLead}
        pmDoc={pmDoc}
        pmNotes={pmNotes}
        // Removed quote props
        // quote={quote}
        // quoteLoading={quoteLoading}
        // log form (controlled)
        called={called}
        onCalledChange={setCalled}
        answered={answered}
        onAnsweredChange={setAnswered}
        priceOffered={priceOffered}
        onPriceOfferedChange={setPriceOffered}
        newPrice={newPrice}
        onNewPriceChange={setNewPrice}
        notes={notes}
        onNotesChange={setNotes}
        nextCallDate={nextCallDate}
        onNextCallDateChange={setNextCallDate}
        // actions
        onSave={handleSaveAttempt}
        onOpenLead={activeLead ? () => navigate(`/leads/${activeLead.leadId}`) : undefined}
        onOpenSalesReport={openSalesReportForActive}
      />

      {/* DNC Modal */}
      <Modal isOpen={dncOpen} onClose={closeDnc} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark as Dead (Do Not Contact)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              This will remove the lead from active reports and block future calls/emails.
            </Text>
            <textarea
              value={dncReason}
              onChange={(e) => setDncReason(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid var(--chakra-colors-gray-200)',
              }}
            />
            <Box h={2} />
            <Text fontSize="sm" color="gray.600">
              Notes (optional)
            </Text>
            <textarea
              value={dncNote}
              onChange={(e) => setDncNote(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid var(--chakra-colors-gray-200)',
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeDnc}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDnc}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}